import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { ButtonGroup } from '@optools/components/ui/button-group';
import { Calendar } from '@optools/components/ui/calendar';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@optools/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@optools/components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { Separator } from '@optools/components/ui/separator';
import { formatHourMinuteSecond, formatYearMonthDay } from '@optools/utils/datetime';
import { getHours, getMinutes, getSeconds, parse, set } from 'date-fns';
import { CalendarIcon, Clock2Icon, RotateCcw, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { DateRange, TZDate } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const DEFAULT_START_TIME = '00:00:00';
export const DEFAULT_END_TIME = '23:59:59';

function createDateWithTime(date: Date, timeString: string, milliseconds: number) {
  const parsed = parse(timeString, 'HH:mm:ss', new TZDate());

  return new TZDate(
    set(date, {
      hours: getHours(parsed),
      milliseconds,
      minutes: getMinutes(parsed),
      seconds: getSeconds(parsed),
    }),
  );
}

export interface AuditTableDateFilterProps {
  endDate?: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;
  startDate?: string | null;
}

export function AuditTableDateFilter({
  endDate,
  onDateChange,
  startDate,
}: AuditTableDateFilterProps) {
  const [open, setOpen] = useState(false);
  const selectedRange = {
    from: startDate ? new TZDate(startDate) : undefined,
    to: endDate ? new TZDate(endDate) : undefined,
  };

  const startTime = startDate ? formatHourMinuteSecond(startDate) : DEFAULT_START_TIME;
  const endTime = endDate ? formatHourMinuteSecond(endDate) : DEFAULT_END_TIME;

  const handleDateChange = (value: DateRange | undefined) => {
    if (!value) return;
    const { from, to } = value;

    if (from && to) {
      const startDateWithTime = createDateWithTime(from, startTime, 0);
      const endDateWithTime = createDateWithTime(to, endTime, 999);

      onDateChange(startDateWithTime.toISOString(), endDateWithTime.toISOString());
    }
  };

  const handleApplyTime = (values: TimeFormValues) => {
    if (!selectedRange?.from || !selectedRange?.to) return;

    const startDateWithTime = createDateWithTime(selectedRange.from, values.startTime, 0);
    const endDateWithTime = createDateWithTime(selectedRange.to, values.endTime, 999);

    onDateChange(startDateWithTime.toISOString(), endDateWithTime.toISOString());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ButtonGroup className="**:text-xs">
        {startDate && (
          <Button size="icon-sm" variant="outline" onClick={() => onDateChange(null, null)}>
            <XCircle />
            <span className="sr-only">Clear Date Range</span>
          </Button>
        )}
        <PopoverTrigger asChild>
          <Button
            aria-label="Date Range"
            className="px-2 py-1 transition-colors"
            role="combobox"
            size="sm"
            variant="outline">
            <CalendarIcon />
            <span>
              {startDate && endDate ? (
                <>
                  {formatYearMonthDay(startDate)}
                  {' - '}
                  {formatYearMonthDay(endDate)}
                </>
              ) : (
                'Date Range'
              )}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" alignOffset={startDate ? -32 : 0} className="w-auto p-0">
          <Calendar
            defaultMonth={selectedRange.from}
            min={0}
            mode="range"
            numberOfMonths={2}
            required={false}
            selected={selectedRange}
            onSelect={(value) => handleDateChange(value)}
          />
          <Separator />
          <TimeSelectionForm
            disabled={!selectedRange?.from || !selectedRange?.to}
            endTime={endTime}
            startTime={startTime}
            onSubmit={handleApplyTime}
          />
        </PopoverContent>
      </ButtonGroup>
    </Popover>
  );
}
const timeFormSchema = z.object({
  endTime: z.iso.time(),
  startTime: z.iso.time(),
});

type TimeFormValues = z.infer<typeof timeFormSchema>;

interface TimeSelectionFormProps {
  disabled: boolean;
  endTime: string;
  onSubmit: (values: TimeFormValues) => void;
  startTime: string;
}

function TimeSelectionForm({ disabled, endTime, onSubmit, startTime }: TimeSelectionFormProps) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const form = useForm<TimeFormValues>({
    defaultValues: { endTime, startTime },
    disabled,
    resolver: zodResolver(timeFormSchema),
  });

  const startTimeValue = form.watch('startTime');
  const endTimeValue = form.watch('endTime');

  const applyButtonDisabled = startTimeValue === startTime && endTimeValue === endTime;

  return (
    <Form {...form}>
      <form
        ref={formRef}
        className="flex items-end gap-2 p-4"
        onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="startTime"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="text-xs">Start Time</FormLabel>
              <InputGroup>
                <InputGroupAddon>
                  <Clock2Icon />
                </InputGroupAddon>
                <FormControl>
                  <InputGroupInput
                    {...field}
                    className="w-max appearance-none pl-8 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    step="1"
                    type="time"
                  />
                </FormControl>
                {startTimeValue !== DEFAULT_START_TIME && (
                  <InputGroupAddon align="inline-end" className="animate-fade-in">
                    <InputGroupButton
                      aria-label="Reset"
                      size="icon-xs"
                      title="Reset"
                      type="button"
                      onClick={() => {
                        form.setValue('startTime', DEFAULT_START_TIME);
                      }}>
                      <RotateCcw />
                    </InputGroupButton>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endTime"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="text-xs">End Time</FormLabel>
              <InputGroup>
                <InputGroupAddon>
                  <Clock2Icon />
                </InputGroupAddon>
                <FormControl>
                  <InputGroupInput
                    {...field}
                    className="[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    step="1"
                    type="time"
                  />
                </FormControl>
                {endTimeValue !== DEFAULT_END_TIME && (
                  <InputGroupAddon align="inline-end" className="animate-fade-in">
                    <InputGroupButton
                      aria-label="Reset"
                      size="icon-xs"
                      title="Reset"
                      type="button"
                      onClick={() => {
                        form.setValue('endTime', DEFAULT_END_TIME);
                      }}>
                      <RotateCcw />
                    </InputGroupButton>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </FormItem>
          )}
        />
        <Button disabled={applyButtonDisabled || form.formState.disabled} type="submit">
          Apply
        </Button>
      </form>
    </Form>
  );
}
